import React, { useMemo } from 'react';
import ManCrossPageFooter from '../components/ManCrossPageFooter';

// ── Constants ──────────────────────────────────────────────────────────────────
const IPRA_THRESHOLD = 75;

// ── Score semaphore helpers ────────────────────────────────────────────────────
const getScoreConfig = (score) => {
    if (score < 65) return { color: '#F3202A', fontWeight: 700, badgeBg: '#F3202A', badgeText: '#fff', badgeLabel: 'ROJO' };
    if (score < 75) return { color: '#F48732', fontWeight: 400, badgeBg: '#F48732', badgeText: '#fff', badgeLabel: 'NARANJA' };
    if (score < 85) return { color: '#c19400', fontWeight: 400, badgeBg: '#D4AC0D', badgeText: '#574400', badgeLabel: 'AMARILLO' };
    return           { color: '#62C190', fontWeight: 400, badgeBg: '#62C190', badgeText: '#fff', badgeLabel: 'VERDE' };
};

// ── Data builders ──────────────────────────────────────────────────────────────

/**
 * Build dimension rows sorted by impactDim DESC.
 * impactDim = max(0, 75 - score) * weightPercent / 100
 */
const buildDimRows = (ipraTotals) => {
    const avgByDim = ipraTotals?.average_by_dimension || {};

    return Object.entries(avgByDim)
        .map(([key, dim]) => {
            const score         = typeof dim?.average        === 'number' ? dim.average        : null;
            const weightPercent = typeof dim?.weight_percent === 'number' ? dim.weight_percent : null;
            const weightFraction = typeof dim?.weight        === 'number' ? dim.weight
                                 : weightPercent != null ? weightPercent / 100 : null;

            if (score == null) return null;

            const roundedScore = Math.round(score * 10) / 10;
            const gap          = Math.round((roundedScore - IPRA_THRESHOLD) * 10) / 10;
            const impactDim    = roundedScore < IPRA_THRESHOLD && weightPercent != null
                ? (IPRA_THRESHOLD - roundedScore) * (weightPercent / 100)
                : 0;

            // Short display name: trim after "/" and extract code + title
            const title = dim?.title || key;
            const shortTitle = title.split('/')[0].trim();

            return {
                key,
                title,
                shortTitle,
                fullLabel: `${key} · ${shortTitle}`,
                score: roundedScore,
                weightPercent: weightPercent != null ? Math.round(weightPercent) : null,
                weightFraction,
                gap,
                impactDim,
            };
        })
        .filter(Boolean)
        .sort((a, b) => b.impactDim - a.impactDim);
};

/** Auto-generated callout title: "[Dim1] y [Dim2] concentran el X% del déficit…" */
const buildCalloutText = (rows) => {
    if (rows.length < 2) return '';

    const totalImpact = rows.reduce((acc, r) => acc + r.impactDim, 0);
    const [d1, d2] = rows;
    const top2Impact = d1.impactDim + d2.impactDim;
    const concPct   = totalImpact > 0 ? Math.round((top2Impact / totalImpact) * 100) : 0;
    const pesoSum   = (d1.weightPercent || 0) + (d2.weightPercent || 0);

    return `${d1.shortTitle} y ${d2.shortTitle} concentran el ${concPct}% del déficit de liderazgo — y pesan el ${pesoSum}% del IPRA.`;
};

/** Auto-generated footer: "Mejorar [Dim1] de X a 75 sube el IPRA global en ~Y puntos…" */
const buildFooterText = (rows) => {
    if (!rows.length) return '';
    const d1 = rows[0];
    if (d1.score >= IPRA_THRESHOLD || d1.weightFraction == null) return '';

    const gapToThreshold = IPRA_THRESHOLD - d1.score;
    const pts = Math.round(gapToThreshold * d1.weightFraction * 10) / 10;

    return `Mejorar ${d1.shortTitle} de ${Math.round(d1.score)} a ${IPRA_THRESHOLD} sube el IPRA global en ~${pts} puntos, el mayor retorno por dimensión de todo el informe.`;
};

// ── Component ──────────────────────────────────────────────────────────────────

/**
 * S07 — Dimensiones IPRA
 *
 * Props:
 *  - reportData          : full report payload
 *  - headerText          : top-bar context string
 *  - pageNumber          : displayed page number
 *  - impactDescriptions  : optional string[3] — editorial descriptions for the
 *                          "Impacto Combinado" right panel top-3 items.
 *                          When not provided the description line is omitted.
 */
const ManCrossPageDimIPRA = ({
    reportData,
    headerText,
    pageNumber,
    impactDescriptions = [],
}) => {
    const ipraTotals = reportData?.payload?.totals?.ipra || {};

    const rows       = useMemo(() => buildDimRows(ipraTotals),    [ipraTotals]);
    const callout    = useMemo(() => buildCalloutText(rows),       [rows]);
    const footerText = useMemo(() => buildFooterText(rows),        [rows]);
    const top3       = rows.slice(0, 3);  // top 3 by impactDim DESC → right panel
    const tableRows  = useMemo(() => [...rows].sort((a, b) => a.score - b.score), [rows]); // score ASC → table

    return (
        <div className="mgcr-di-page">

            {/* Page header bar */}
            <div className="mgcr-page-header">
                <span className="mgcr-page-header-text">{headerText}</span>
                <span className="mgcr-page-header-num">{pageNumber}</span>
            </div>

            <div className="keep-blankspace" />

            {/* Title */}
            <div className="mgcr-dx-title-area">
                <h2 className="mgcr-dx-main-title">Dimensiones IPRA</h2>
                <p  className="mgcr-dx-subtitle">Impacto por Gestión</p>
            </div>

            {/* ── Main board ── */}
            <div className="mgcr-di-board">

                {/* LEFT: Desglose de Dimensiones */}
                <div className="mgcr-di-left">

                    {/* Callout header row */}
                    <div className="mgcr-di-callout-row">
                        <div className="mgcr-di-section-label">Desglose de<br />Dimensiones</div>
                        <div className="mgcr-di-callout-text">{callout}</div>
                    </div>

                    {/* Column headers */}
                    <div className="mgcr-di-col-header">
                        <div className="mgcr-di-ch-name">Dimensión IPRA</div>
                        <div className="mgcr-di-ch-weight">Peso</div>
                        <div className="mgcr-di-ch-bar">
                            {/* Axis ticks */}
                            <div className="mgcr-di-axis">
                                {[0, 25, 50, 75, 100].map(v => (
                                    <span key={v}>{v}</span>
                                ))}
                            </div>
                        </div>
                        <div className="mgcr-di-ch-score">Score</div>
                        <div className="mgcr-di-ch-gap">Brecha</div>
                    </div>

                    {/* Dimension rows — ordenadas score ASC (menor → mayor) */}
                    <div className="mgcr-di-rows">
                        {tableRows.map((row) => {
                            const cfg    = getScoreConfig(row.score);
                            const gapNeg = row.gap < 0;

                            return (
                                <div key={row.key} className="mgcr-di-row">

                                    {/* Dim name — solo título, sin prefijo de código */}
                                    <div className="mgcr-di-name">
                                        <span className="mgcr-di-dim-title">{row.shortTitle}</span>
                                    </div>

                                    {/* Weight */}
                                    <div className="mgcr-di-weight">
                                        {row.weightPercent != null ? `${row.weightPercent}%` : '—'}
                                    </div>

                                    {/* Bar track */}
                                    <div className="mgcr-di-bar-col">
                                        <div className="mgcr-di-bar-track">
                                            <div
                                                className="mgcr-di-bar-fill"
                                                style={{
                                                    width: `${Math.min(Math.max(row.score, 0), 100)}%`,
                                                    background: cfg.badgeBg,
                                                    opacity: 0.85,
                                                }}
                                            />
                                            {/* Threshold line at 75% */}
                                            <div className="mgcr-di-threshold-tick" />
                                        </div>
                                    </div>

                                    {/* Score value con % */}
                                    <div
                                        className="mgcr-di-score-val"
                                        style={{ color: cfg.color, fontWeight: cfg.fontWeight }}
                                    >
                                        {Math.round(row.score)}%
                                    </div>

                                    {/* Brecha */}
                                    <div
                                        className="mgcr-di-gap"
                                        style={{
                                            background: gapNeg ? '#FFE5E5' : '#E8F8F0',
                                            color:      gapNeg ? '#F3202A' : '#62C190',
                                            fontWeight: gapNeg ? 700 : 400,
                                        }}
                                    >
                                        {row.gap > 0 ? `+${row.gap}` : row.gap}
                                    </div>

                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* RIGHT: Impacto Combinado */}
                <div className="mgcr-di-right">
                    <p className="mgcr-di-impact-header">Impacto Combinado</p>

                    <div className="mgcr-di-impact-items">
                        {top3.map((row, idx) => {
                            const isFirst = idx === 0;
                            const desc    = impactDescriptions[idx];
                            const gapAbs  = Math.abs(Math.round(row.gap));
                            const cfg     = getScoreConfig(row.score);

                            return (
                                <div
                                    key={row.key}
                                    className={`mgcr-di-impact-item${isFirst ? ' mgcr-di-impact-item--first' : ''}`}
                                >
                                    <span className="mgcr-di-impact-rank">{idx + 1}</span>
                                    <div className="mgcr-di-impact-body">
                                        <p className="mgcr-di-impact-name">
                                            {row.shortTitle}
                                            {row.weightPercent != null && (
                                                <span className="mgcr-di-impact-weight"> · {row.weightPercent}%</span>
                                            )}
                                        </p>
                                        <p className="mgcr-di-impact-gap-label" style={{ color: cfg.color }}>
                                            {row.gap < 0
                                                ? `Score ${Math.round(row.score)} — ${gapAbs} pts bajo umbral`
                                                : `Score ${Math.round(row.score)} — ${gapAbs} pts sobre umbral`}
                                        </p>
                                        {desc && (
                                            <p className="mgcr-di-impact-desc">{desc}</p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

            </div>

            {/* Footer */}
            {footerText && (
                <div className="mgcr-insight-footer">
                    <strong>Esto implica que:</strong>{' '}{footerText}
                </div>
            )}

            <div className="keep-blankspace" />

            <ManCrossPageFooter />
        </div>
    );
};

export default ManCrossPageDimIPRA;
